import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./LeftSidebarLayout.css";
import familySafeLogo from "../assets/images/family-safe-logo.png";
import { post } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import Footer from "../components/Footer/Footer";
import HelpPopup from "../components/HelpPopUp";
import EssentialStorageAlert from "../components/EssentialStorageAlert";
import PromotionExpiryNotice from "../components/PromotionExpiryNotice";
import MessagePopup from "../components/MessagePopup";
import FeedbackPopup from "../components/FeedbackPopup";
import GlobalCardSearch, { SearchIcon } from "../components/GlobalCardSearch";
import ProfileMenuControl from "../components/ProfileMenuControl";
import { ChatWidgetProvider } from "../context/ChatWidgetProvider";
import { useAuthPortal } from "../context/AuthPortalContext";
import { useBranding } from "../context/BrandingContext";
import { useCategories } from "../context/useCategories";
import { useMenu } from "../context/useMenu";
import { useProfile } from "../context/ProfileContext";
import { useChatWidget } from "../context/useChatWidget";
import { CategoryData, Menu, MenuSub } from "../helper/interface";
import { navigateToDashboardCategory } from "../helper/categoryNavigation";
import DefaultMenuIcon from "../icons/default.svg?react";

const LEGACY_MENU_ROUTE_MAP: Record<string, string> = {
  "/nourl12": "/probate-exports",
};

const menuIconModules = {
  ...import.meta.glob("../icons/*.svg", { eager: true, import: "default" }),
  ...import.meta.glob("../assets/**/*.svg", { eager: true, import: "default" }),
};

const menuIconSrcByName = Object.entries(menuIconModules).reduce<Record<string, string>>(
  (iconMap, [path, iconSrc]) => {
    const fileName = path.split("/").pop();
    if (!fileName) {
      return iconMap;
    }

    const normalizedName = fileName.replace(/\.svg$/i, "").toLowerCase();
    iconMap[normalizedName] = iconSrc as string;
    return iconMap;
  },
  {}
);

const renderMenuIcon = (iconName?: string | null) => {
  const normalizedName = iconName?.trim().replace(/\.svg$/i, "").toLowerCase();
  const iconSrc = normalizedName ? menuIconSrcByName[normalizedName] : undefined;

  if (!iconSrc) {
    return <DefaultMenuIcon className="left-sidebar-nav-icon" aria-hidden="true" />;
  }

  return (
    <img
      src={iconSrc}
      alt=""
      aria-hidden="true"
      className="left-sidebar-nav-icon"
    />
  );
};

const normalizeActionUri = (actionUri?: string | null) => {
  if (!actionUri) return null;
  const normalizedActionUri = actionUri.startsWith("/") ? actionUri : `/${actionUri}`;
  return LEGACY_MENU_ROUTE_MAP[normalizedActionUri.toLowerCase()] ?? normalizedActionUri;
};

const TopProfileBar = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const { menus } = useMenu();
  const { profile } = useProfile();
  const chatWidget = useChatWidget();
  const location = useLocation();
  const currentPath = location.pathname.toLowerCase();
  const routeState = location.state as { category?: CategoryData } | null;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const fullName = profile?.fullName ?? "";

  const pageTitle = useMemo(() => {
    if (routeState?.category?.categoryName) {
      return routeState.category.categoryName;
    }

    if (currentPath === "/") {
      return "Dashboard";
    }

    for (const menu of menus) {
      const menuRoute = normalizeActionUri(menu.actionUri)?.toLowerCase();
      if (menuRoute && menuRoute !== "/" && currentPath.startsWith(menuRoute)) {
        return menu.label;
      }

      const matchingSubMenu = (menu.subMenus ?? []).find((subMenu) => {
        const subMenuRoute = normalizeActionUri(subMenu.actionUri)?.toLowerCase();
        return subMenuRoute && subMenuRoute !== "/" && currentPath.startsWith(subMenuRoute);
      });

      if (matchingSubMenu) {
        return matchingSubMenu.label;
      }
    }

    return "Dashboard";
  }, [currentPath, menus, routeState?.category?.categoryName]);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [location.key]);

  return (
    <header className="left-sidebar-topbar">
      <button
        type="button"
        className="left-sidebar-mobile-toggle"
        onClick={onMenuClick}
        aria-label="Open navigation"
      >
        <span />
        <span />
        <span />
      </button>
      <h1 className="left-sidebar-page-title">{pageTitle}</h1>
      <div className="left-sidebar-topbar-spacer" />
      <button
        type="button"
        className={`navbar-search-button ${isSearchOpen ? "navbar-search-button--active" : ""}`}
        onClick={() => setIsSearchOpen((prev) => !prev)}
        aria-label="Search cards and templates"
        aria-expanded={isSearchOpen}
      >
        <SearchIcon />
      </button>
      <button
        type="button"
        className="left-sidebar-ask-familysafe"
        onClick={() => chatWidget.expand()}
        aria-label="Ask FamilySafe"
        title="Ask FamilySafe"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </button>
      <div className="left-sidebar-topbar-account">
        <ProfileMenuControl />
        <span className="left-sidebar-topbar-account-copy">
          <span className="left-sidebar-topbar-account-name">{fullName || "Your account"}</span>
          <span className="left-sidebar-topbar-account-hint">Account &amp; sign out</span>
        </span>
      </div>
      <GlobalCardSearch
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </header>
  );
};

interface SidebarNavigationProps {
  isOpen: boolean;
  onClose: () => void;
}

const SidebarNavigation = ({ isOpen, onClose }: SidebarNavigationProps) => {
  const { menus, loading, hasAccess } = useMenu();
  const { categories, loading: categoriesLoading } = useCategories();
  const { profile, refreshProfile } = useProfile();
  const { branding } = useBranding();
  const { portal } = useAuthPortal();
  const chatWidget = useChatWidget();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUnavailablePopup, setShowUnavailablePopup] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [expandedOverviewMenus, setExpandedOverviewMenus] = useState<Set<string>>(() => new Set());

  const logoSrc = portal?.partner?.logoFile ? `/assets/logos/${portal.partner.logoFile}` : familySafeLogo;

  const fullName = profile?.fullName ?? "";
  const email = profile?.email ?? "";

  const householdLabel = profile?.familyName?.trim()
    || (fullName.trim() ? `${fullName.trim().split(" ")[0]}'s household` : "Your household");
  const householdInitial = (fullName.trim() || householdLabel).charAt(0).toUpperCase() || "F";

  const handleRestartOnboarding = async () => {
    try {
      await post(Routes.TAKE_ON_RESTART, {});
      await refreshProfile();
      navigate("/take-on");
    } catch (error) {
      console.error("Failed to restart onboarding:", error);
    }
  };

  const overviewMenus = useMemo(
    () => [...menus]
      .filter((menu: Menu) => menu.isProfile !== true)
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [menus]
  );

  const sortedCategories = useMemo(
    () => [...(categories ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [categories]
  );

  const activeCategoryId = (location.state as { category?: CategoryData } | null)?.category?.id_Category;
  const currentPath = location.pathname.toLowerCase();

  const isMenuItemActive = (actionUri?: string | null) => {
    const target = normalizeActionUri(actionUri);
    return target
      ? (target === "/" ? currentPath === "/" : currentPath.startsWith(target.toLowerCase()))
      : false;
  };

  const isMenuGroupActive = (menu: Menu) => (
    isMenuItemActive(menu.actionUri) ||
    (menu.subMenus ?? []).some((subMenu) => isMenuItemActive(subMenu.actionUri))
  );

  useEffect(() => {
    setExpandedOverviewMenus((current) => {
      const next = new Set(current);
      overviewMenus.forEach((menu) => {
        if ((menu.subMenus?.length ?? 0) > 0 && isMenuGroupActive(menu)) {
          next.add(menu.id);
        }
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, overviewMenus]);

  const handleMenuAction = (actionUri?: string | null) => {
    const normalizedActionUri = normalizeActionUri(actionUri);
    if (!normalizedActionUri) return;
    const uri = normalizedActionUri.toLowerCase();

    if (uri.includes("/help/")) {
      chatWidget.expand();
      onClose();
      return;
    }

    if (uri === "/feedback") {
      setShowFeedbackPopup(true);
      onClose();
      return;
    }

    if (uri.includes("nourl") || uri.includes("comingsoon")) {
      setShowUnavailablePopup(true);
      onClose();
      return;
    }

    if (uri === "/partner" && !hasAccess("/partner")) return;

    navigate(normalizedActionUri, { state: normalizedActionUri === "/" ? { resetCard: true } : undefined });
    onClose();
  };

  const renderMenuButton = (item: Menu | MenuSub, isChild = false) => {
    const isActive = isMenuItemActive(item.actionUri);

    return (
      <button
        type="button"
        key={item.id}
        className={`left-sidebar-nav-item${isActive ? " is-active" : ""}${isChild ? " left-sidebar-nav-item--child" : ""}`}
        onClick={() => handleMenuAction(item.actionUri)}
      >
        {isChild
          ? <span className="left-sidebar-submenu-dot" aria-hidden="true" />
          : renderMenuIcon(item.icon)}
        <span>{item.label}</span>
      </button>
    );
  };

  const renderOverviewMenu = (menu: Menu) => {
    const sortedSubMenus = [...(menu.subMenus ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

    if (sortedSubMenus.length === 0) {
      return renderMenuButton(menu);
    }

    const isExpanded = expandedOverviewMenus.has(menu.id);
    const isActive = isMenuGroupActive(menu);

    return (
      <React.Fragment key={menu.id}>
        <button
          type="button"
          className={`left-sidebar-nav-item left-sidebar-nav-item--collapsible${isActive ? " is-active" : ""}`}
          onClick={() => {
            setExpandedOverviewMenus((current) => {
              const next = new Set(current);
              if (next.has(menu.id)) {
                next.delete(menu.id);
              } else {
                next.add(menu.id);
              }
              return next;
            });
          }}
          aria-expanded={isExpanded}
        >
          {renderMenuIcon(menu.icon)}
          <span>{menu.label}</span>
          <span className="left-sidebar-chevron" aria-hidden="true" />
        </button>
        {isExpanded && (
          <div className="left-sidebar-submenu">
            {sortedSubMenus.map((subMenu) => renderMenuButton(subMenu, true))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const handleCategoryClick = async (category: CategoryData) => {
    await navigateToDashboardCategory({
      category,
      navigate,
      location,
      showCreateCard: false,
    });
    onClose();
  };

  return (
    <>
      <aside className={`left-sidebar${isOpen ? " is-open" : ""}`} aria-label="Primary navigation">
        <button
          type="button"
          className="left-sidebar-brand"
          onClick={() => navigate("/", { state: { resetCard: true } })}
          aria-label="Go to dashboard"
        >
          <img src={logoSrc} alt={branding?.brandName || "FamilySafe"} />
        </button>
        <nav className="left-sidebar-nav">
          {profile?.takeOnCompleted === false && (
            <button
              type="button"
              className="left-sidebar-household-card"
              onClick={() => void handleRestartOnboarding()}
            >
              <span className="left-sidebar-household-avatar" aria-hidden="true">{householdInitial}</span>
              <span className="left-sidebar-household-copy">
                <span className="left-sidebar-household-name">{householdLabel}</span>
                <span className="left-sidebar-household-action">Restart onboarding</span>
              </span>
              <svg
                className="left-sidebar-household-chevron"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m7 15 5 5 5-5" />
                <path d="m7 9 5-5 5 5" />
              </svg>
            </button>
          )}

          <section className="left-sidebar-section">
            <h2>Overview</h2>
            {loading ? (
              <span className="left-sidebar-muted">Loading...</span>
            ) : (
              overviewMenus.map((menu) => renderOverviewMenu(menu))
            )}
          </section>

          <section className="left-sidebar-section">
            <h2>Your Life</h2>
            {categoriesLoading ? (
              <span className="left-sidebar-muted">Loading...</span>
            ) : sortedCategories.length === 0 ? (
              <span className="left-sidebar-muted">No categories yet</span>
            ) : (
              sortedCategories.map((category) => (
                <button
                  type="button"
                  key={category.id_Category}
                  className={`left-sidebar-nav-item left-sidebar-category${activeCategoryId === category.id_Category ? " is-active" : ""}`}
                  onClick={() => void handleCategoryClick(category)}
                >
                  <span
                    className="left-sidebar-category-swatch"
                    style={{ background: category.colourStart || "var(--brand-front-colour, #0f766e)" }}
                    aria-hidden="true"
                  />
                  <span>{category.categoryName}</span>
                </button>
              ))
            )}
          </section>
        </nav>
      </aside>

      <button
        type="button"
        className={`left-sidebar-overlay${isOpen ? " is-open" : ""}`}
        onClick={onClose}
        aria-label="Close navigation"
      />

      <MessagePopup show={showUnavailablePopup} onClose={() => setShowUnavailablePopup(false)} />
      <FeedbackPopup
        show={showFeedbackPopup}
        onClose={() => setShowFeedbackPopup(false)}
        userName={fullName}
        userEmail={email}
      />
    </>
  );
};

interface LeftSidebarLayoutProps {
  children?: React.ReactNode;
}

const LeftSidebarLayout = ({ children }: LeftSidebarLayoutProps) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { branding } = useBranding();
  const layoutClassName = branding?.layout ? `left-sidebar-layout--layout${branding.layout}` : "";

  return (
    <ChatWidgetProvider>
      <div className={`left-sidebar-layout ${layoutClassName}`.trim()}>
        <TopProfileBar onMenuClick={() => setIsSidebarOpen(true)} />
        <SidebarNavigation isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className="left-sidebar-content">
          <div className="left-sidebar-alert">
            <EssentialStorageAlert />
          </div>
          <main className="left-sidebar-main">
            <PromotionExpiryNotice />
            {children ?? <Outlet />}
          </main>
          <Footer />
        </div>
        <HelpPopup />
      </div>
    </ChatWidgetProvider>
  );
};

export default LeftSidebarLayout;
